import { useState } from 'react';
import { useSEO } from '../../utils/seo';

export default function ContactPage() {
  // SEO Configuration
  useSEO({
    title: 'Contact Izislide - Designer PowerPoint | Demandez un Devis Gratuit',
    description: 'Imagez vos idées : Contactez Izislide pour vos projets de présentations PowerPoint. Designer PowerPoint expert. Devis gratuit sous 48h. Email, téléphone ou formulaire de contact.',
    keywords: 'contact izislide, devis présentation powerpoint, designer powerpoint contact, demande information, agence powerpoint contact',
    canonical: '/contact',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      mainEntity: {
        '@type': 'Organization',
        name: 'Izislide',
        slogan: 'Imagez vos idées',
        description: 'Designer PowerPoint expert en création de présentations PowerPoint professionnelles',
        telephone: '+33-1-23-45-67-89',
        email: 'contact@izislide.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '123 Avenue des Champs-Élysées',
          addressLocality: 'Paris',
          postalCode: '75008',
          addressCountry: 'FR'
        },
        openingHoursSpecification: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00'
        }
      }
    }
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('Merci pour votre message ! Nous vous répondrons dans les plus brefs délais.');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section 
        className="relative py-24 overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,168,107,0.9) 0%, rgba(0,143,92,0.9) 100%), url('https://public.readdy.ai/ai/img_res/f3bc669d8d3259d97c1e8a3b98a935e4.jpg')`
        }}
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Parlons de votre projet de présentation PowerPoint
            </h1>
            <p className="text-xl opacity-90">
              Notre designer PowerPoint vous répond sous 24h. <strong>Imagez vos idées</strong> avec nous !
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Envoyez-nous un message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#00A86B] focus:outline-none transition-colors"
                      placeholder="Jean Dupont"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#00A86B] focus:outline-none transition-colors"
                      placeholder="jean.dupont@exemple.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#00A86B] focus:outline-none transition-colors"
                      placeholder="+33 1 23 45 67 89"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-gray-700 font-semibold mb-2">
                      Entreprise
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#00A86B] focus:outline-none transition-colors"
                      placeholder="Nom de votre entreprise"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-gray-700 font-semibold mb-2">
                      Service souhaité *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#00A86B] focus:outline-none transition-colors pr-8 cursor-pointer"
                    >
                      <option value="">Sélectionnez un service</option>
                      <option value="creation">Création de présentations PowerPoint</option>
                      <option value="refonte">Refonte de slides PowerPoint existantes</option>
                      <option value="templates">Templates PowerPoint personnalisés</option>
                      <option value="formation">Formation PowerPoint</option>
                      <option value="partenariat">Partenariat agence - Designer PowerPoint</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#00A86B] focus:outline-none transition-colors resize-none"
                      placeholder="Décrivez-nous votre projet de présentation PowerPoint..."
                    ></textarea>
                    <p className="text-sm text-gray-500 mt-2">
                      {formData.message.length}/500 caractères
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-[#00A86B] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#008f5c] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    Envoyer le message
                  </button>
                </form>
              </div>

              {/* Contact Info */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Nos coordonnées
                </h2>
                
                <div className="space-y-6 mb-12">
                  <div className="flex items-start">
                    <div className="w-14 h-14 bg-[#F7A348]/10 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                      <i className="ri-mail-line text-2xl text-[#F7A348]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Email</h3>
                      <a href="mailto:contact@izislide.com" className="text-gray-600 hover:text-[#00A86B] transition-colors cursor-pointer">
                        contact@izislide.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-14 h-14 bg-[#00A86B]/10 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                      <i className="ri-phone-line text-2xl text-[#00A86B]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Téléphone</h3>
                      <a href="tel:+33123456789" className="text-gray-600 hover:text-[#00A86B] transition-colors cursor-pointer">
                        +33 1 23 45 67 89
                      </a>
                      <p className="text-sm text-gray-500 mt-1">
                        Lun - Ven : 9h - 18h
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-14 h-14 bg-[#C9D86A]/30 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                      <i className="ri-map-pin-line text-2xl text-[#00A86B]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Adresse</h3>
                      <p className="text-gray-600">
                        123 Avenue des Champs-Élysées<br />
                        75008 Paris, France
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-8 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Temps de réponse
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-time-line text-[#00A86B] text-xl mr-3"></i>
                      <span className="text-gray-700">
                        <strong>Email :</strong> Sous 24h
                      </span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-phone-line text-[#00A86B] text-xl mr-3"></i>
                      <span className="text-gray-700">
                        <strong>Téléphone :</strong> Immédiat
                      </span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-calendar-line text-[#00A86B] text-xl mr-3"></i>
                      <span className="text-gray-700">
                        <strong>Devis PowerPoint :</strong> Sous 48h
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Suivez-nous
                  </h3>
                  <div className="flex space-x-4">
                    <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#00A86B] hover:text-white transition-all cursor-pointer">
                      <i className="ri-linkedin-fill text-xl"></i>
                    </a>
                    <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#00A86B] hover:text-white transition-all cursor-pointer">
                      <i className="ri-twitter-x-fill text-xl"></i>
                    </a>
                    <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#00A86B] hover:text-white transition-all cursor-pointer">
                      <i className="ri-facebook-fill text-xl"></i>
                    </a>
                    <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#00A86B] hover:text-white transition-all cursor-pointer">
                      <i className="ri-instagram-fill text-xl"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ rapide */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Questions fréquentes
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl">
                <h3 className="font-bold text-lg text-gray-900 mb-2">
                  Quels sont vos délais pour les présentations PowerPoint ?
                </h3>
                <p className="text-gray-600">
                  Standard : 48-72h. Express : 24h possible selon disponibilités de notre designer PowerPoint.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="font-bold text-lg text-gray-900 mb-2">
                  Comment se passe la collaboration ?
                </h3>
                <p className="text-gray-600">
                  Brief initial sur votre présentation PowerPoint, création par notre designer PowerPoint, allers-retours jusqu'à validation finale.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="font-bold text-lg text-gray-900 mb-2">
                  Proposez-vous des abonnements ?
                </h3>
                <p className="text-gray-600">
                  Oui, formules mensuelles pour besoins récurrents de présentations PowerPoint avec tarifs préférentiels.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="font-bold text-lg text-gray-900 mb-2">
                  Travaillez-vous à distance ?
                </h3>
                <p className="text-gray-600">
                  Oui, 100% de nos projets de présentations PowerPoint peuvent être gérés à distance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}